import { Component, OnInit, ViewChild, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ChatService } from 'src/app/service/chat/chat.service';
import { IonRefresher, IonInfiniteScroll, ModalController } from '@ionic/angular';
import { IChat } from 'src/app/interface/chat';
import { AlertService } from 'src/app/service/alert/alert.service';
import { ChatUpdatedService } from 'src/app/event/chat-updated/chat-updated.service';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/service/auth/auth.service';
import { Push } from 'src/app/helper/push/push-helper';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Badge } from '@capawesome/capacitor-badge';


@Component({
	selector: 'app-chats',
	templateUrl: './chats.component.html',
	styleUrls: ['./chats.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatsComponent implements OnInit, OnDestroy {

	private userChatUpdatedSubscription: Subscription;
	private groupChatUpdatedSubscription: Subscription;

	public requesting: boolean;
	private page: number;
	public chats: Array<IChat>;

	@ViewChild(IonRefresher)
	public ionRefresher: IonRefresher;
	@ViewChild(IonInfiniteScroll)
	public ionInfiniteScroll: IonInfiniteScroll;
	public nextPage: boolean = true;

	@Output()
	public chat = new EventEmitter<IChat>();

	constructor(
		private chatService: ChatService,
		private alertService: AlertService,
		private modalController: ModalController,
		private chatUpdatedService: ChatUpdatedService,
		private authService: AuthService,
		private changeDetectorRef: ChangeDetectorRef
	) { }

	public ngOnInit(): void {
		this.index(true);
		this.chatUpdatedListeners();
	}

	public ngOnDestroy(): void {
		if (this.userChatUpdatedSubscription) {
			this.userChatUpdatedSubscription.unsubscribe();
		}
		if (this.groupChatUpdatedSubscription) {
			this.groupChatUpdatedSubscription.unsubscribe();
		}
	}

	private chatUpdatedListeners() {
		this.userChatUpdatedSubscription = this.chatUpdatedService
			.listener('user', this.authService.user?.id)
			.subscribe(response => this.updateChat(response.chat));
		this.groupChatUpdatedSubscription = this.chatUpdatedService
			.listener('group', this.authService.user?.id)
			.subscribe(response => this.updateChat(response.chat));
	}

	private updateChat(newChat: IChat) {
		if (!this.chats) {
			this.chats = [];
		}
		const currentChat = this.chats.find(item => item.id === newChat.id);
		if (currentChat) {
			currentChat.unread_messages = newChat.unread_messages;
			currentChat.message = newChat.message;
		} else {
			this.chats.unshift(newChat);
		}
		this.updateBadge(newChat?.unread_messages);
	}

	private updateBadge(count?: number) {
		this.changeDetectorRef.detectChanges();
		if (count) {
			Badge.set({ count });
		} else {
			Badge.clear();
			LocalNotifications.removeAllDeliveredNotifications();
			Push.removeAllDeliveredNotifications();
		}
	}

	public index(reset?: boolean) {
		if (reset) {
			this.page = 1;
		}
		if (this.page <= 0 || this.requesting) {
			this.ionRefresher.disabled = false;
			this.ionInfiniteScroll.disabled = false;
			this.ionInfiniteScroll.complete();
			return;
		}
		this.requesting = true;
		this.changeDetectorRef.detectChanges();
		this.chatService
			.index(this.page)
			.subscribe(
				{
					next: (response) => {
						if (!this.chats || reset) {
							this.chats = [];
						}
						if (response.next_page_url) {
							this.page = response.current_page + 1;
						} else {
							this.page = 0;
						}
						response.data?.forEach(chat => this.chats.push(chat));

						this.nextPage = !!response.next_page_url;
					},
					error: (error) => this.alertService.error(error)
				}

			)
			.add(async () => {
				this.requesting = false;
				this.ionRefresher.disabled = false;
				await this.ionInfiniteScroll.complete();
				this.ionInfiniteScroll.disabled = !this.nextPage
				this.changeDetectorRef.detectChanges();
			});
	}

	public dismiss() {
		this.modalController.dismiss();
	}

	public openChat(chat?: IChat) {
		this.chat.next(chat);
	}

}
